import { useMemo, useState, useCallback } from 'react'
import type { TransitGraph, TransitNode, TransitEdge } from '../../hooks/useIntelligence'

const COLORS = {
  node: '#5A78FF',
  nodeHover: '#3854d2',
  edgeLight: '#bac8ff',
  edgeMed: '#748ffc',
  edgeHeavy: '#4263eb',
  text: '#1c2a6a',
  bg: '#f8f9fc',
  violation: '#ef4444',
}

const TYPE_COLORS: Record<string, string> = {
  Ride: '#5A78FF',
  Game: '#20B99D',
  'Food Stand': '#f59e0b',
  'Food': '#f59e0b',
  unknown: '#8697A8',
}

function nodeColor(type: string) {
  return TYPE_COLORS[type] || TYPE_COLORS.unknown
}

function formatTime(sec: number) {
  if (sec >= 60) return `${Math.floor(sec / 60)}m ${sec % 60}s`
  return `${sec}s`
}

function formatDist(m: number) {
  return m >= 1000 ? `${(m / 1000).toFixed(1)}km` : `${m}m`
}

interface TransitMapProps {
  graph: TransitGraph
}

export function TransitMap({ graph }: TransitMapProps) {
  const [hoveredNode, setHoveredNode] = useState<number | null>(null)
  const [selectedNode, setSelectedNode] = useState<number | null>(null)
  const [showLabels, setShowLabels] = useState(true)

  const SVG_SIZE = 660
  const maxScans = useMemo(() => Math.max(...graph.nodes.map(n => n.total_scans), 1), [graph])
  const maxObs = useMemo(() => Math.max(...graph.edges.map(e => e.observations), 1), [graph])

  const nodeRadius = useCallback((n: TransitNode) => {
    return 8 + Math.sqrt(n.total_scans / maxScans) * 20
  }, [maxScans])

  const edgeWidth = useCallback((e: TransitEdge) => {
    return 0.5 + (e.observations / maxObs) * 5
  }, [maxObs])

  const edgeColor = useCallback((e: TransitEdge) => {
    const ratio = e.observations / maxObs
    if (ratio > 0.5) return COLORS.edgeHeavy
    if (ratio > 0.2) return COLORS.edgeMed
    return COLORS.edgeLight
  }, [maxObs])

  const nodeById = useMemo(() => {
    const m = new Map<number, TransitNode>()
    for (const n of graph.nodes) m.set(n.id, n)
    return m
  }, [graph])

  // Filter edges to show only those connected to selected/hovered node, or top edges
  const visibleEdges = useMemo(() => {
    if (selectedNode != null) {
      return graph.edges.filter(e => e.from === selectedNode || e.to === selectedNode)
    }
    if (hoveredNode != null) {
      return graph.edges.filter(e => e.from === hoveredNode || e.to === hoveredNode)
    }
    // Show top edges by traffic (avoid visual clutter)
    return graph.edges.slice(0, 80)
  }, [graph, hoveredNode, selectedNode])

  const activeNode = selectedNode ?? hoveredNode

  // Info panel for active node
  const activeNodeData = activeNode != null ? nodeById.get(activeNode) : null
  const activeNodeEdges = useMemo(() => {
    if (activeNode == null) return []
    return graph.edges
      .filter(e => e.from === activeNode || e.to === activeNode)
      .map(e => {
        const otherId = e.from === activeNode ? e.to : e.from
        const other = nodeById.get(otherId)
        return { ...e, otherName: other?.name ?? '?', otherId }
      })
      .sort((a, b) => a.distance_m - b.distance_m)
  }, [activeNode, graph, nodeById])

  return (
    <div className="mm-card p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-ink-dark">Carnival Layout — Transit Map</h3>
          <p className="text-[11px] text-ink-soft mt-0.5">
            Ride positions derived from scan chain walking times via MDS projection.
            {graph.triangle_violations > 0 && (
              <span className="ml-2 text-amber-600">
                {graph.triangle_violations} triangle inequality violation{graph.triangle_violations > 1 ? 's' : ''} detected
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-[11px] text-ink-soft cursor-pointer">
            <input
              type="checkbox"
              checked={showLabels}
              onChange={() => setShowLabels(!showLabels)}
              className="rounded border-hairline text-butler-600 focus:ring-butler-600/30"
            />
            Labels
          </label>
          {selectedNode != null && (
            <button
              onClick={() => setSelectedNode(null)}
              className="text-[11px] text-butler-600 hover:text-butler-700 font-medium"
            >
              Clear selection
            </button>
          )}
        </div>
      </div>

      <div className="flex gap-4">
        {/* SVG Map */}
        <div className="flex-1 relative" style={{ minHeight: SVG_SIZE }}>
          <svg
            viewBox={`0 0 ${SVG_SIZE} ${SVG_SIZE}`}
            className="w-full h-auto rounded-lg border border-hairline"
            style={{ background: COLORS.bg }}
          >
            {/* Edges */}
            {visibleEdges.map((e, i) => {
              const from = nodeById.get(e.from)
              const to = nodeById.get(e.to)
              if (!from || !to) return null
              const isActive = activeNode != null && (e.from === activeNode || e.to === activeNode)
              return (
                <line
                  key={`e-${i}`}
                  x1={from.x + 30} y1={from.y + 30}
                  x2={to.x + 30} y2={to.y + 30}
                  stroke={isActive ? COLORS.edgeHeavy : edgeColor(e)}
                  strokeWidth={isActive ? edgeWidth(e) * 1.5 : edgeWidth(e)}
                  strokeOpacity={isActive ? 0.8 : 0.3}
                  strokeLinecap="round"
                />
              )
            })}

            {/* Distance labels on active edges */}
            {activeNode != null && activeNodeEdges.slice(0, 8).map((e, i) => {
              const from = nodeById.get(e.from)
              const to = nodeById.get(e.to)
              if (!from || !to) return null
              const mx = (from.x + to.x) / 2 + 30
              const my = (from.y + to.y) / 2 + 30
              return (
                <text
                  key={`dl-${i}`}
                  x={mx} y={my - 4}
                  textAnchor="middle"
                  className="select-none pointer-events-none"
                  style={{ fontSize: 9, fill: COLORS.text, fontWeight: 600 }}
                >
                  {formatDist(e.distance_m)}
                </text>
              )
            })}

            {/* Nodes */}
            {graph.nodes.map(node => {
              const r = nodeRadius(node)
              const isActive = node.id === activeNode
              const isConnected = activeNode != null && activeNodeEdges.some(e => e.otherId === node.id)
              const opacity = activeNode == null ? 1 : (isActive || isConnected ? 1 : 0.25)
              return (
                <g
                  key={node.id}
                  transform={`translate(${node.x + 30}, ${node.y + 30})`}
                  style={{ cursor: 'pointer', opacity }}
                  onMouseEnter={() => setHoveredNode(node.id)}
                  onMouseLeave={() => setHoveredNode(null)}
                  onClick={() => setSelectedNode(prev => prev === node.id ? null : node.id)}
                >
                  <circle
                    r={r}
                    fill={isActive ? COLORS.nodeHover : nodeColor(node.type)}
                    fillOpacity={0.85}
                    stroke="white"
                    strokeWidth={isActive ? 3 : 1.5}
                  />
                  {showLabels && (
                    <text
                      y={r + 12}
                      textAnchor="middle"
                      className="select-none pointer-events-none"
                      style={{
                        fontSize: 10,
                        fill: COLORS.text,
                        fontWeight: isActive ? 700 : 500,
                      }}
                    >
                      {node.name.length > 16 ? node.name.slice(0, 14) + '…' : node.name}
                    </text>
                  )}
                </g>
              )
            })}
          </svg>
        </div>

        {/* Info Panel */}
        <div className="w-64 shrink-0 space-y-3">
          {activeNodeData ? (
            <>
              <div className="p-3 rounded-lg bg-butler-600/5 border border-butler-600/20">
                <div className="text-sm font-semibold text-ink-dark">{activeNodeData.name}</div>
                <div className="mt-1 text-[11px] text-ink-soft space-y-0.5">
                  <div>{activeNodeData.total_scans.toLocaleString()} total scans</div>
                  <div>{activeNodeData.traffic.toLocaleString()} transit observations</div>
                </div>
              </div>
              <div className="text-[10px] font-semibold uppercase tracking-wide text-ink-soft">
                Nearest Rides ({activeNodeEdges.length})
              </div>
              <div className="space-y-1 max-h-[400px] overflow-y-auto">
                {activeNodeEdges.map((e, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between px-2 py-1.5 rounded text-[11px] hover:bg-surface-hover cursor-pointer"
                    onClick={() => setSelectedNode(e.otherId)}
                  >
                    <span className="text-ink truncate max-w-[120px]" title={e.otherName}>
                      {e.otherName}
                    </span>
                    <div className="flex items-center gap-2 tabular-nums">
                      <span className="text-ink-soft">{formatDist(e.distance_m)}</span>
                      <span className="text-ink-soft/60">{formatTime(e.p5_sec)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="p-3 rounded-lg bg-surface border border-hairline text-center">
              <p className="text-xs text-ink-soft">Click a ride to see distances and transit times to nearby attractions.</p>
              <div className="mt-3 space-y-1 text-[10px] text-ink-soft/70">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#5A78FF]" />
                  <span>Node size = total scans</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-0.5 bg-[#4263eb]" />
                  <span>Edge thickness = traffic</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-0.5 bg-[#bac8ff]" />
                  <span>Light = low traffic</span>
                </div>
              </div>
            </div>
          )}

          {/* Type legend */}
          <div className="pt-2 border-t border-hairline space-y-1">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-ink-soft">Type</div>
            {Object.entries(TYPE_COLORS).filter(([k]) => k !== 'unknown').map(([type, color]) => (
              <div key={type} className="flex items-center gap-2 text-[10px] text-ink-soft">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
                <span>{type}</span>
              </div>
            ))}
          </div>

          {/* Stats footer */}
          <div className="pt-2 border-t border-hairline text-[10px] text-ink-soft space-y-0.5">
            <div>{graph.node_count} attractions · {graph.edge_count} pairs</div>
            <div>Distances from P5 transit × 0.97 m/s</div>
            {graph.triangle_violations > 0 && (
              <div className="text-amber-600">
                {graph.triangle_violations} triangle inequality violation{graph.triangle_violations > 1 ? 's' : ''} (10% tolerance)
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Proximity-Revenue Correlation */}
      {graph.proximity_revenue.length > 0 && (
        <div className="mt-4 pt-4 border-t border-hairline">
          <h4 className="text-xs font-semibold text-ink-dark mb-1">Proximity-Revenue Analysis — Games & Food Near Top Rides</h4>
          <p className="text-[10px] text-ink-soft mb-3">
            Does placing a game or food stand near a popular ride increase its revenue?
            {graph.top_rides_for_proximity.length > 0 && (
              <> Measured against: {graph.top_rides_for_proximity.map(r => r.name).join(', ')}</>
            )}
          </p>
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead>
                <tr className="border-b border-hairline bg-[#FAFBFB]">
                  <th className="px-3 py-1.5 text-left text-[10px] font-semibold uppercase tracking-[1px] text-ink-soft">Attraction</th>
                  <th className="px-3 py-1.5 text-left text-[10px] font-semibold uppercase tracking-[1px] text-ink-soft">Type</th>
                  <th className="px-3 py-1.5 text-right text-[10px] font-semibold uppercase tracking-[1px] text-ink-soft">Avg Dist to Rides</th>
                  <th className="px-3 py-1.5 text-right text-[10px] font-semibold uppercase tracking-[1px] text-ink-soft">Revenue</th>
                  <th className="px-3 py-1.5 text-right text-[10px] font-semibold uppercase tracking-[1px] text-ink-soft">Scans</th>
                  <th className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[1px] text-ink-soft">$/m</th>
                </tr>
              </thead>
              <tbody>
                {graph.proximity_revenue.map((p, i) => {
                  const revenuePerMeter = p.avg_dist_to_top_rides_m > 0
                    ? (p.revenue / p.avg_dist_to_top_rides_m).toFixed(2)
                    : '—'
                  return (
                    <tr key={p.id} className={`border-b border-hairline/50 ${i % 2 === 1 ? 'bg-[#FAFBFB]' : ''}`}>
                      <td className="px-3 py-1.5 font-medium text-ink-dark max-w-[140px] truncate" title={p.name}>{p.name}</td>
                      <td className="px-3 py-1.5">
                        <span className="inline-flex items-center gap-1">
                          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: nodeColor(p.type) }} />
                          <span className="text-ink-soft">{p.type}</span>
                        </span>
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-ink">{formatDist(p.avg_dist_to_top_rides_m)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums font-medium text-ink-dark">
                        ${p.revenue.toLocaleString()}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-ink-soft">{p.total_scans.toLocaleString()}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-ink-soft">{revenuePerMeter}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <p className="text-[10px] text-ink-soft/60 mt-2">
            $/m = revenue per meter of distance from top rides. Higher values near short distances suggest proximity drives revenue.
          </p>
        </div>
      )}
    </div>
  )
}
